/**
 * **Die Löcher im Schleier** — die Stellen, an denen die Fenstersteuerung
 * durchscheint und anklickbar bleibt.
 *
 * Der Schleier des **Geschafft-Moments** liegt über dem ganzen Fenster:
 * Inhalt, Tabs, Titelleiste. Nur **Ampel** (macOS) beziehungsweise
 * **Schließen** (Windows) bleiben frei — ein Programm, dessen Schließen-Knopf
 * nicht mehr geht, fühlt sich wie eine Web-Seite an.
 *
 * **Warum Löcher und kein Hochstapeln:** Die Ampel steckt in
 * `.titlebar__left-controls`, und das trägt `z-index: 3` als Flex-Kind — ein
 * eigener Stapelkontext. Nichts darin kann über einen Schleier steigen, der als
 * Geschwister des Wurzelknotens liegt. Die Gruppe hochzuziehen hieße, auch
 * Seitenleisten-Schalter und Lupe mit hochzuziehen; die Leiste selbst
 * hochzuziehen hieße, die Tabs unter dem Schleier hervorzuholen — und genau die
 * sollen darunter liegen.
 *
 * `clip-path` umgeht das ganz: Was weggeschnitten ist, wird **weder gezeichnet
 * noch getroffen**. Der Klick fällt durch das Loch auf die Ampel darunter, und
 * es gibt keinen einzigen `z-index` zu pflegen.
 *
 * **Warum je Knopf ein Kreis und keine Pille um alle drei** (gemeldet
 * 23.09.2026): Die Kante eines Lochs wird geglättet — dort deckt der Schleier
 * nur zum Teil. Lag sie auf der hellen Titelleiste, blitzte um die Ampel eine
 * feine helle Linie, auch mit einem getönten Ring darunter (zwei geglättete
 * Kanten am selben Ort ergeben nie eine ganze). Ein Kreis, der etwas KLEINER
 * ist als der Knopf, hat seine Kante auf der Farbe des Knopfs: Dort kann nichts
 * Helles durchscheinen.
 */

/** Um so viel ist ein Kreis kleiner als sein Knopf — die geglättete Kante liegt auf dem Knopf. */
export const VEIL_CIRCLE_INSET = 0.5

/** macOS: die drei Ampelknöpfe. */
const TRAFFIC_LIGHT_SELECTOR = '.traffic-light'
/** Windows: der Schließen-Knopf. */
const CLOSE_SELECTOR = '.titlebar__control--close'

export type VeilHole =
  | { kind: 'circle'; cx: number; cy: number; r: number }
  | { kind: 'rect'; left: number; top: number; width: number; height: number }

function holePath(hole: VeilHole): string | null {
  if (hole.kind === 'circle') {
    const { cx, cy, r } = hole
    if (r <= 0) return null
    // Zwei Halbkreise — ein einzelner Bogen von einem Punkt zu sich selbst zeichnet nichts.
    return `M${cx - r},${cy} a${r},${r} 0 1,0 ${r * 2},0 a${r},${r} 0 1,0 ${-r * 2},0 Z`
  }
  if (hole.width <= 0 || hole.height <= 0) return null
  /*
    Auf ganze Pixel: Gerade Kanten auf ganzen Pixeln glättet der Browser nicht —
    der Schleier endet dann scharf, und keine Naht zeigt die helle Leiste.
  */
  const left = Math.round(hole.left)
  const top = Math.round(hole.top)
  const right = Math.round(hole.left + hole.width)
  const bottom = Math.round(hole.top + hole.height)
  return `M${left},${top} H${right} V${bottom} H${left} Z`
}

/**
 * Baut den `clip-path` für den Schleier: das ganze Fenster **minus** die Löcher.
 *
 * Gibt `undefined` zurück, wenn es nichts auszusparen gibt (im Web etwa, oder
 * wenn die Steuerung gerade nicht gemessen werden kann) — dann deckt der
 * Schleier eben alles zu, was richtig ist statt kaputt.
 *
 * @param holes Die Löcher, gemessen mit {@link measureVeilHoles}.
 * @param viewport Fensterbreite und -höhe. Der Schleier liegt `fixed` auf
 *   `inset: 0`, seine eigenen Koordinaten sind also die des Fensters.
 */
export function buildVeilClip(
  holes: readonly VeilHole[],
  viewport: { width: number; height: number },
): string | undefined {
  const paths = holes.map(holePath).filter((path): path is string => path !== null)
  if (paths.length === 0) return undefined

  const outer = `M0,0 H${viewport.width} V${viewport.height} H0 Z`
  // `evenodd`: Jeder innere Pfad stanzt aus dem äußeren heraus.
  return `path(evenodd, '${outer} ${paths.join(' ')}')`
}

/**
 * Misst die Fenstersteuerung: je Ampelknopf ein Kreis (macOS), sonst ein
 * Rechteck um Schließen (Windows), im Web nichts.
 */
export function measureVeilHoles(): VeilHole[] {
  if (typeof document === 'undefined') return []

  const lights = [...document.querySelectorAll(TRAFFIC_LIGHT_SELECTOR)]
    .map(light => light.getBoundingClientRect())
    .filter(rect => rect.width > 0 && rect.height > 0)
  if (lights.length > 0) {
    return lights.map(rect => ({
      kind: 'circle',
      cx: rect.left + rect.width / 2,
      cy: rect.top + rect.height / 2,
      r: Math.min(rect.width, rect.height) / 2 - VEIL_CIRCLE_INSET,
    }))
  }

  const close = document.querySelector(CLOSE_SELECTOR)?.getBoundingClientRect()
  if (close && close.width > 0 && close.height > 0) {
    return [{ kind: 'rect', left: close.left, top: close.top, width: close.width, height: close.height }]
  }
  return []
}
